/**
 * 🔐 HASH UTILITIES
 *
 * Utilitários de hashing para auditoria e imutabilidade.
 * Usa a Web Crypto API nativa — compatível com browser e Electron.
 * Não requer dependências externas.
 */

/**
 * Serialização estável (chaves ordenadas, recursiva).
 * Garante que o mesmo objeto sempre produz o mesmo JSON,
 * independente da ordem de inserção das propriedades.
 */
function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return String(value);
  if (typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return '[' + value.map(stableStringify).join(',') + ']';
  }
  const obj = value as Record<string, unknown>;
  const sortedKeys = Object.keys(obj).sort();
  const entries = sortedKeys.map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`);
  return '{' + entries.join(',') + '}';
}

/**
 * Gera um SHA-256 hex-digest de qualquer valor serializável.
 *
 * @param data - Objeto ou primitivo a ser hasheado
 * @returns SHA-256 hex string (64 chars)
 *
 * @example
 * const hash = await sha256({ name: 'Blueprint', stages: [...] });
 * // "a3f7b2..." (64 chars)
 */
export async function sha256(data: unknown): Promise<string> {
  const serialized = stableStringify(data);
  const encoded = new TextEncoder().encode(serialized);
  const hashBuffer = await crypto.subtle.digest('SHA-256', encoded);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Versão síncrona baseada em djb2 — use APENAS para fallbacks offline
 * onde crypto.subtle não está disponível. NUNCA use para auditoria real.
 */
export function djb2Hash(data: unknown): string {
  const str = stableStringify(data);
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
    hash = hash >>> 0; // Converter para unsigned 32-bit
  }
  return hash.toString(16).padStart(8, '0');
}
